import { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { projectContext, loadingContext, urlValue, unfolder, useTranslate } from '../../lib'
import SideBarGenAI from '../../components/ProjectGenAI/SideBarGenAI'
import TitleBar from '../../components/TitleBar'
import { Alerts } from '../../components/common/Alerts'
import '../../styles/settings.scss'
import '../../styles/styles.css'

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx']
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

export default function GuideGenAI() {
  const [selectedProject] = useContext(projectContext)
  const [loading, setLoading] = useContext(loadingContext)
  const { t } = useTranslate()

  const [existingGuide, setExistingGuide] = useState(null)
  const [details, setDetails] = useState('')
  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState('')
  const [alert, setAlert] = useState({ title: '', visible: false })

  useEffect(() => {
    fetchGuide()
  }, [])

  const getAuthHeaders = () => {
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token
    const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
    return { token, id, csrfToken }
  }

  const fetchGuide = async () => {
    try {
      const { token, id } = getAuthHeaders()
      setLoading(true)
      const response = await axios({
        method: 'get',
        url: `${urlValue}/studio/guide/project/${selectedProject.id}`,
        headers: { user: id, 'access-token': token }
      })
      const data = response.data.data
      const guide = Array.isArray(data) && data.length > 0 ? data[0] : null
      setExistingGuide(guide)
      if (guide) setDetails(guide.details || '')
    } catch (error) {
      console.error('Error fetching guide:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateFile = (selectedFile) => {
    if (!selectedFile) return ''
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return t('guide.invalidFileType') || 'Tipo inválido. Permitidos: jpg, jpeg, png, pdf, doc, docx'
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      return t('guide.fileTooLarge') || 'Archivo muy grande. Máximo 20MB.'
    }
    return ''
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    const error = validateFile(selectedFile)
    setFileError(error)
    setFile(error ? null : selectedFile)
  }

  const handleSave = async () => {
    if (!details.trim() && !file && !existingGuide?.file_url) return
    try {
      const { token, id, csrfToken } = getAuthHeaders()
      setLoading(true)

      const formData = new FormData()
      formData.append('details', details)
      if (file) formData.append('file', file)

      if (existingGuide) {
        await axios({
          method: 'post',
          url: `${urlValue}/studio/guide/${existingGuide.id}/update`,
          headers: { user: id, 'access-token': token, 'X-CSRF-Token': csrfToken },
          withCredentials: true,
          data: formData,
        })
      } else {
        formData.append('project_id', selectedProject.id)
        formData.append('created_by', id)
        await axios({
          method: 'post',
          url: `${urlValue}/studio/guide`,
          headers: { user: id, 'access-token': token, 'X-CSRF-Token': csrfToken },
          withCredentials: true,
          data: formData,
        })
      }

      setFile(null)
      const fileInput = document.getElementById('guide-file-input')
      if (fileInput) fileInput.value = ''
      setFileError('')

      setAlert({ title: t('guide.saved') || 'Guía guardada exitosamente', visible: true })
      setTimeout(() => setAlert({ title: '', visible: false }), 4000)
      await fetchGuide()
    } catch (error) {
      console.error('Error saving guide:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!existingGuide?.file_url) return
    try {
      const { token, id } = getAuthHeaders()
      const response = await axios({
        method: 'get',
        url: `${urlValue}/studio/guide/${existingGuide.id}/download`,
        headers: { user: id, 'access-token': token },
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', existingGuide.file_name)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }

  const isSaveDisabled = !details.trim() && !file && !existingGuide?.file_url

  return (
    <>
      <section className="container-settings general-container" id="container">
        <SideBarGenAI active={'Guide'} />
        <div className="container">
          <div className="con_title_bar">
            <TitleBar />
          </div>
          <div className="container-info w-100 d-flex flex-row flex-wrap justify-space-between">
            <div className="d-flex flex-row align-items-center flex-wrap justify-space-between w-100">
              <div className="title d-flex flex-row align-items-center ml-2" style={{ whiteSpace: 'nowrap' }}>
                <div><h2>{selectedProject.name}</h2></div>
                <div className="p-1 separator">-</div>
                <div><h1>{t('common.guide') || 'Guide'}</h1></div>
              </div>
            </div>

            <div className="container-edit" style={{ width: '100%', marginTop: 24 }}>
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: '28px 32px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}>
                {/* Details */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, display: 'block', color: '#333' }}>
                    {t('guide.details') || 'Details'}
                  </label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={5}
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      padding: '10px 12px',
                      fontSize: 14,
                      resize: 'vertical',
                      outline: 'none',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                    placeholder={t('guide.detailsPlaceholder') || 'Ingrese los detalles para la guía...'}
                  />
                </div>

                {/* File upload */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, display: 'block', color: '#333' }}>
                    {t('guide.file') || 'Archivo'}
                    <span style={{ fontSize: 11, color: '#999', fontWeight: 400, marginLeft: 6 }}>
                      (jpg, jpeg, png, pdf, doc, docx — máx. 20MB)
                    </span>
                  </label>

                  {/* Current file indicator */}
                  {existingGuide?.file_name && !file && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#f5f8ff',
                      border: '1px solid #d0e0ff',
                      borderRadius: 8,
                      padding: '8px 14px',
                      marginBottom: 10,
                      fontSize: 13,
                      color: '#444',
                    }}>
                      <span>📎 {existingGuide.file_name}</span>
                      <button
                        onClick={handleDownload}
                        style={{
                          marginLeft: 'auto',
                          background: '#2196F3',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 12px',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        ⬇ {t('guide.download') || 'Descargar'}
                      </button>
                    </div>
                  )}

                  <input
                    id="guide-file-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{ fontSize: 13 }}
                  />
                  {file && (
                    <p style={{ fontSize: 12, color: '#2196F3', marginTop: 4 }}>
                      📎 {file.name}
                    </p>
                  )}
                  {fileError && (
                    <p style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{fileError}</p>
                  )}
                </div>

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    style={{
                      background: isSaveDisabled ? '#ccc' : '#2196F3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 28px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                    }}
                  >
                    {t('common.save') || 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {alert.visible && <Alerts id="alert-guide" title={alert.title} />}
        </div>
      </section>
    </>
  )
}
